import { useEffect } from 'react';

const DEFAULT_TITLE = 'Savin Hood | UK Tax Calculator';

interface DocumentMetaOptions {
  /** Page title - will be appended with " | Savin Hood" */
  title?: string;
  /** Meta description for SEO */
  description?: string;
  /** Canonical URL for SEO - prevents duplicate content issues */
  canonical?: string;
}

/**
 * Hook to manage document title, meta description, and canonical link.
 *
 * Sets the document title, meta description, and canonical link,
 * and restores defaults on unmount.
 *
 * @example
 * useDocumentMeta({
 *   title: '1257L Tax Code Explained',
 *   description: 'Learn what the 1257L tax code means...',
 *   canonical: 'https://app.savinhood.com/tax-codes/1257L'
 * });
 */
export function useDocumentMeta({ title, description, canonical }: DocumentMetaOptions): void {
  useEffect(() => {
    // Set title
    const previousTitle = document.title;
    if (title) {
      document.title = `${title} | Savin Hood`;
    }

    // Set or update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    const previousDescription = metaDescription?.getAttribute('content') ?? null;

    if (description) {
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', description);
    }

    // Set or update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    const previousCanonical = canonicalLink?.getAttribute('href') ?? null;

    if (canonical) {
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonical);
    }

    // Cleanup: restore previous values on unmount
    return () => {
      document.title = previousTitle || DEFAULT_TITLE;
      if (metaDescription && previousDescription !== null) {
        metaDescription.setAttribute('content', previousDescription);
      } else if (metaDescription && description) {
        // Remove the meta tag if we created it
        metaDescription.remove();
      }
      if (canonicalLink && previousCanonical !== null) {
        canonicalLink.setAttribute('href', previousCanonical);
      } else if (canonicalLink && canonical) {
        // Remove the canonical link if we created it
        canonicalLink.remove();
      }
    };
  }, [title, description, canonical]);
}
